import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ChromadbService {

  private baseUrl = environment.apiUrl; // 'http://127.0.0.1:8228'; // Cambia esto si la URL o el puerto son diferentes

  constructor(private http: HttpClient) {}

  /**
   * Elimina una colección específica en el servidor.
   * @param collection Nombre de la colección a eliminar.
   * @returns Observable con la respuesta del servidor.
   */
  deleteCollection(collection: string): Observable<any> {
    const url = `${this.baseUrl}/delete_data/${collection}`;
    return this.http.delete(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  /**
   * Obtiene la lista de colecciones disponibles en el servidor con soporte para límite, paginación y filtros.
   * @param params Parámetros opcionales: limit, offset, search, start_date, end_date.
   * @returns Observable con la lista de colecciones.
   */
  getCollections(params?: {
    limit?: number | null;
    offset?: number | null;
    search?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }): Observable<any> {
    let url = `${this.baseUrl}/get_collections`;
    if (params) {
      const searchParams = new URLSearchParams();
      if (params.limit !== undefined && params.limit !== null && params.limit > 0) {
        searchParams.append('limit', params.limit.toString());
      }
      if (params.offset !== undefined && params.offset !== null && params.offset >= 0) {
        searchParams.append('offset', params.offset.toString());
      }
      if (params.search && params.search.trim()) {
        searchParams.append('search', params.search.trim());
      }
      if (params.start_date && params.start_date.trim()) {
        searchParams.append('start_date', params.start_date.trim());
      }
      if (params.end_date && params.end_date.trim()) {
        searchParams.append('end_date', params.end_date.trim());
      }
      const qs = searchParams.toString();
      if (qs) {
        url += `?${qs}`;
      }
    }
    return this.http.get(url);
  }

  /**
   * Obtiene los datos de una colección específica.
   * @param name Nombre de la colección.
   * @returns Observable con los datos de la colección.
   */
  getCollection(name: string): Observable<any> {
    const url = `${this.baseUrl}/get_collection/${name}`;
    return this.http.get(url);
  }

  /**
   * Sincroniza y recalcula el número de documentos de una colección en sus metadatos.
   * @param collection Nombre de la colección.
   * @returns Observable con el nuevo conteo y la fecha actualizada.
   */
  syncCollectionCount(collection: string): Observable<any> {
    const url = `${this.baseUrl}/sync_collection_count/${collection}`;
    return this.http.post(url, {});
  }

  /**
   * Elimina un elemento específico en una colección.
   * @param collection Nombre de la colección.
   * @param id ID del elemento a eliminar.
   * @returns Observable con la respuesta del servidor.
   */
  deleteElement(collection: string, id: string): Observable<any> {
    const url = `${this.baseUrl}/delete_element/${collection}/${id}`;
    return this.http.delete(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  /**
   * Edita el nombre de una colección específica.
   * @param collection Nombre de la colección.
   * @param newName Nuevo nombre de la colección.
   * @param metadata Metadatos opcionales de la colección.
   * @returns Observable con la respuesta del servidor.
   */
  editCollectionName(collection: string, newName: string, metadata?: Record<string, any>): Observable<any> {
    const url = `${this.baseUrl}/edit_collection_name/${collection}`;
    const payload: any = { name: newName };
    if (metadata) {
      payload.metadata = metadata;
    }
    return this.http.post(url, payload, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  /**
   * Edita un elemento específico en una colección.
   * @param collection Nombre de la colección.
   * @param id ID del elemento a editar.
   * @param data Datos actualizados del elemento.
   * @returns Observable con la respuesta del servidor.
   */
  editElement(collection: string, id: number, data: any): Observable<any> {
    if (id<0) {
      // Create new document
      const url = `${this.baseUrl}/${collection}/create_document`;
      return this.http.post(url, data, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      });
    } else {
      // Update document
      const url = `${this.baseUrl}/edit_element/${collection}/${id}`;
      return this.http.post(url, data, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      });
    }
  }

  /**
   * Crea una nueva colección en el servidor.
   * @param name Nombre de la colección.
   * @param embedding_function Función de embedding a utilizar.
   * @param distance_function Función de distancia a utilizar.
   * @param metadata Metadatos opcionales de la colección.
   * @returns Observable con la respuesta del servidor.
   */
  createCollection(name: string, embedding_function: string, distance_function: string, metadata?: Record<string, any>): Observable<any> {
    const url = `${this.baseUrl}/create_collection`;
    const payload: any = { name: name, function: embedding_function, distance: distance_function };
    if (metadata) {
      payload.metadata = metadata;
    }
    return this.http.post(url, payload, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }
  
  /**
   * Busca una frase en una colección específica.
   * @param collection Nombre de la colección.
   * @param query Frase a buscar.
   * @returns Observable con la respuesta del servidor.
   */
  searchCollection(collection: string, query: string, limit: number): Observable<any> {
    const url = `${this.baseUrl}/${collection}/search?q=${query}&n=${limit}`;
    return this.http.get(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

    /**
   * Obtiene la lista de todos los topics disponibles en los metadatos
   * @param collectionName Opcional. Si se proporciona, filtra por la colección especificada.
   * @param startDate Opcional. Fecha de inicio (YYYY-MM-DD).
   * @param endDate Opcional. Fecha de fin (YYYY-MM-DD).
   * @param collections Opcional. Lista o cadena de nombres de colecciones separadas por comas.
   * @returns Observable con la lista de topics.
   */
    getTopics(collectionName?: string, startDate?: string, endDate?: string, collections?: string[] | string): Observable<any> {
    let url = `${this.baseUrl}/topics`;
    if (collectionName) {
      url = `${this.baseUrl}/topics/${collectionName}`;
    }
    
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (collections) {
      const colStr = Array.isArray(collections) ? collections.join(',') : collections;
      if (colStr && colStr.trim()) {
        params.append('collections', colStr.trim());
      }
    }
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    return this.http.get(url);
  }

}
